import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Stepper,
    Group,
    Button,
    Stack,
    Text,
    Alert,
    Divider,
    Paper,
    SimpleGrid,
    TextInput,
    Select,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import {
    TransportRouteBookingInitResponseDto,
    CreateTransportRouteBookingRequest,
    TransportRouteBookingPickupLocationDto,
    TransportRouteBookingPickupOptionDto,
} from "./types";
import { WHATSAPP_COUNTRIES } from "../../data/whatsappCountries";

const apiUrl = process.env.REACT_APP_API_URL;

type Props = {
    init: TransportRouteBookingInitResponseDto;
};

type DropoffOption = {
    transportRouteStopID: number;
    transportRouteLocationID: number;
    locationName: string;
};

type Draft = {
    contactName?: string;
    contactEmail?: string;
    whatsappCountryIso2?: string;
    whatsappCountryCode?: string;
    whatsappNumber?: string;
    transportRouteID?: number;
    transportRouteLocationID?: number;
    transportDepartureID?: number;
    transportDepartureStopID?: number;
    dropoffTransportRouteStopID?: number;
    passengerCount: number;
};

function OptionGrid({
    options,
    value,
    onChange,
    cols,
}: {
    options: { value: string; label: string; description?: string }[];
    value: string | null;
    onChange: (v: string) => void;
    cols?: { base: number; sm: number };
}) {
    return (
        <SimpleGrid cols={cols ?? { base: 1, sm: 2 }} spacing="sm">
            {options.map((o) => {
                const selected = value === o.value;

                return (
                    <Paper
                        key={o.value}
                        withBorder
                        p="md"
                        radius="md"
                        style={{
                            cursor: "pointer",
                            borderColor: selected
                                ? "var(--mantine-primary-color-filled)"
                                : undefined,
                            boxShadow: selected
                                ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                : undefined,
                        }}
                        onClick={() => onChange(o.value)}
                    >
                        <Text fw={600}>{o.label}</Text>
                        {o.description && (
                            <Text size="sm" c="dimmed" mt={4}>
                                {o.description}
                            </Text>
                        )}
                    </Paper>
                );
            })}
        </SimpleGrid>
    );
}

export default function TransportRouteBookingStepper({ init }: Props) {
    const [active, setActive] = useState(0);
    const [draft, setDraft] = useState<Draft>({
        passengerCount: 1,
        transportRouteLocationID: init.prefillRouteLocationID ?? undefined,
    });

    const [pickupLocations, setPickupLocations] = useState<TransportRouteBookingPickupLocationDto[]>([]);
    const [pickupOptions, setPickupOptions] = useState<TransportRouteBookingPickupOptionDto[]>([]);
    const [dropoffOptions, setDropoffOptions] = useState<DropoffOption[]>([]);

    const [loadingPickupOptions, setLoadingPickupOptions] = useState(false);
    const [loadingDropoffOptions, setLoadingDropoffOptions] = useState(false);

    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selectedPickupDate, setSelectedPickupDate] = useState<string | undefined>();

    const isMobile = useMediaQuery("(max-width: 768px)");

    const PRIORITY_ISO2 = [
        "DK",
        "CA",
        "CN",
        "FR",
        "DE",
        "HK",
        "IN",
        "ID",
        "JP",
        "MY",
        "MX",
        "SG",
        "ES",
        "TW",
        "TH",
        "TR",
        "GB",
        "VN",
        "BG",
    ];

    const routeOptions = useMemo(
        () =>
            init.routes.map((r) => ({
                value: String(r.transportRouteID),
                label: r.nameDK,
                description: r.description ?? undefined,
            })),
        [init.routes]
    );

    console.log("init", init);
    console.log("init.routes", init.routes);
    console.log("routeOptions", routeOptions);
    // Brug direkte de locations som API allerede sender tilbage som pickup-egnede
    const pickupLocationOptions = useMemo(
        () =>
            pickupLocations.map((l) => ({
                value: String(l.transportRouteLocationID),
                label: l.locationName,
            })),
        [pickupLocations]
    );

    const pickupDateOptions = useMemo(() => {
        if (!draft.transportRouteLocationID) return [];

        const uniqueDates = Array.from(
            new Set(
                pickupOptions
                    .filter((p) => p.transportRouteLocationID === draft.transportRouteLocationID)
                    .map((p) => p.departureDate)
            )
        );

        return uniqueDates.map((date) => ({
            value: date,
            label: new Date(date).toLocaleDateString("en-GB", {
                weekday: "short",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }),
        }));
    }, [pickupOptions, draft.transportRouteLocationID]);

    const filteredPickupTimeOptions = useMemo(() => {
        if (!draft.transportRouteLocationID || !selectedPickupDate) return [];

        const matchingOptions = pickupOptions.filter(
            (p) =>
                p.transportRouteLocationID === draft.transportRouteLocationID &&
                p.departureDate === selectedPickupDate
        );

        if (matchingOptions.length === 0) return [];

        const sortedOptions = [...matchingOptions].sort((a, b) =>
            a.pickupTime.localeCompare(b.pickupTime)
        );

        const lastOption = sortedOptions[sortedOptions.length - 1];

        return sortedOptions.map((p) => ({
            value: String(p.transportDepartureStopID),
            label:
                p.transportDepartureStopID === lastOption.transportDepartureStopID
                    ? "20 min after last matches"
                    : p.pickupTime,
        }));
    }, [pickupOptions, draft.transportRouteLocationID, selectedPickupDate]);

    const dropoffOptionCards = useMemo(
        () =>
            dropoffOptions
                .filter(
                    (d) => d.transportRouteLocationID !== draft.transportRouteLocationID
                )
                .map((d) => ({
                    value: String(d.transportRouteStopID),
                    label: d.locationName,
                })),
        [dropoffOptions, draft.transportRouteLocationID]
    );

    const whatsappCountryOptions = useMemo(() => {
        const prioritySet = new Set(PRIORITY_ISO2);

        const popularItems = WHATSAPP_COUNTRIES
            .filter((c) => prioritySet.has(c.iso2))
            .sort(
                (a, b) =>
                    PRIORITY_ISO2.indexOf(a.iso2) - PRIORITY_ISO2.indexOf(b.iso2)
            )
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        const restItems = WHATSAPP_COUNTRIES
            .filter((c) => !prioritySet.has(c.iso2))
            .sort((a, b) => a.country.localeCompare(b.country))
            .map((c) => ({
                value: c.iso2,
                label: `${c.code} — ${c.country}`,
            }));

        return [
            { group: "Popular", items: popularItems },
            { group: "All countries", items: restItems },
        ];
    }, []);

    const selectedWhatsappCountry =
        WHATSAPP_COUNTRIES.find((x) => x.code === draft.whatsappCountryCode)?.country ?? null;

    const whatsappValid =
        !!draft.whatsappCountryCode &&
        !!draft.whatsappNumber &&
        draft.whatsappNumber.replace(/\s+/g, "").length >= 6;

    const emailValid = !!draft.contactEmail?.trim()
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())
        : false;

    const contactValid = !!draft.contactName?.trim() && emailValid;
    const passengersValid = draft.passengerCount >= 1 && draft.passengerCount <= 30;

    const selectedPickupOption = useMemo(
        () =>
            pickupOptions.find(
                (p) => p.transportDepartureStopID === draft.transportDepartureStopID
            ),
        [pickupOptions, draft.transportDepartureStopID]
    );

    const selectedDropoffOption = useMemo(
        () =>
            dropoffOptions.find(
                (d) => d.transportRouteStopID === draft.dropoffTransportRouteStopID
            ),
        [dropoffOptions, draft.dropoffTransportRouteStopID]
    );

    const loadPickupOptions = async (routeId: number) => {
        try {
            setLoadingPickupOptions(true);
            setError(null);

            const res = await axios.get(`${apiUrl}/api/transport-route/booking/pickup-options`, {
                params: {
                    eventId: init.event.eventID,
                    routeId,
                    routeLocationId: init.prefillRouteLocationID || undefined,
                },
            });

            const locations = res.data?.locations ?? [];
            const options = res.data?.pickupOptions ?? [];

            setPickupLocations(locations);
            setPickupOptions(options);

            if (init.prefillRouteLocationID) {
                const matchedLocation = locations.find(
                    (l: TransportRouteBookingPickupLocationDto) =>
                        l.transportRouteLocationID === init.prefillRouteLocationID
                );

                if (matchedLocation) {
                    setDraft((d) => ({
                        ...d,
                        transportRouteLocationID: matchedLocation.transportRouteLocationID,
                    }));
                }
            }
        } catch (e: any) {
            console.error(e);
            setError(e?.response?.data ?? "Could not load pickup options.");
        } finally {
            setLoadingPickupOptions(false);
        }
    };

    const loadDropoffOptions = async (routeId: number, routeLocationId: number) => {
        try {
            setLoadingDropoffOptions(true);
            setError(null);

            const res = await axios.get(`${apiUrl}/api/transport-route/booking/dropoff-options`, {
                params: {
                    routeId,
                    routeLocationId,
                },
            });

            setDropoffOptions(res.data ?? []);
        } catch (e: any) {
            console.error(e);
            setError(e?.response?.data ?? "Could not load dropoff options.");
        } finally {
            setLoadingDropoffOptions(false);
        }
    };

    useEffect(() => {
        if (draft.transportRouteID) {
            setSelectedPickupDate(undefined);

            setDraft((d) => ({
                ...d,
                transportRouteLocationID: init.prefillRouteLocationID ?? undefined,
                transportDepartureID: undefined,
                transportDepartureStopID: undefined,
                dropoffTransportRouteStopID: undefined,
            }));

            setPickupLocations([]);
            setPickupOptions([]);
            setDropoffOptions([]);

            loadPickupOptions(draft.transportRouteID);
        } else {
            setSelectedPickupDate(undefined);
            setPickupLocations([]);
            setPickupOptions([]);
            setDropoffOptions([]);
        }
    }, [draft.transportRouteID, init.prefillRouteLocationID]);

    useEffect(() => {
        if (!draft.transportRouteLocationID) return;

        const existsInPickupLocations = pickupLocations.some(
            (l) => l.transportRouteLocationID === draft.transportRouteLocationID
        );

        if (!existsInPickupLocations) {
            setDraft((d) => ({
                ...d,
                transportRouteLocationID: undefined,
                transportDepartureID: undefined,
                transportDepartureStopID: undefined,
                dropoffTransportRouteStopID: undefined,
            }));
            setSelectedPickupDate(undefined);
        }
    }, [pickupLocations, draft.transportRouteLocationID]);

    useEffect(() => {
        if (draft.transportDepartureStopID) {
            const matched = pickupOptions.find(
                (p) => p.transportDepartureStopID === draft.transportDepartureStopID
            );

            if (matched) {
                setDraft((d) => ({
                    ...d,
                    transportDepartureID: matched.transportDepartureID,
                }));
            }
        }
    }, [draft.transportDepartureStopID, pickupOptions]);

    useEffect(() => {
        if (draft.transportRouteID && draft.transportDepartureStopID) {
            const matched = pickupOptions.find(
                (p) => p.transportDepartureStopID === draft.transportDepartureStopID
            );

            if (matched) {
                setDraft((d) => ({
                    ...d,
                    dropoffTransportRouteStopID: undefined,
                }));
                setDropoffOptions([]);
                loadDropoffOptions(draft.transportRouteID, matched.transportRouteLocationID);
                return;
            }
        }

        setDropoffOptions([]);
    }, [draft.transportRouteID, draft.transportDepartureStopID, pickupOptions]);

    const isStepValid = (step: number) => {
        switch (step) {
            case 0:
                return contactValid && whatsappValid;
            case 1:
                return !!draft.transportRouteID;
            case 2:
                return !!draft.transportRouteLocationID;
            case 3:
                return !!draft.transportDepartureStopID && !!draft.transportDepartureID;
            case 4:
                return !!draft.dropoffTransportRouteStopID;
            case 5:
                return passengersValid;
            default:
                return true;
        }
    };

    const next = () => {
        if (active < 6 && isStepValid(active)) {
            setActive((a) => a + 1);
        }
    };

    const prev = () => setActive((a) => Math.max(0, a - 1));

    const submit = async () => {
        if (!draft.transportDepartureID || !draft.transportDepartureStopID) {
            setError("Missing booking data.");
            return;
        }

        if (!draft.dropoffTransportRouteStopID) {
            setError("Please select where you want to get off.");
            return;
        }

        if (!contactValid) {
            setError("Please enter a name and a valid email.");
            return;
        }

        if (!whatsappValid) {
            setError("Please enter a valid WhatsApp number.");
            return;
        }

        const payload: CreateTransportRouteBookingRequest = {
            eventID: init.event.eventID,
            transportDepartureID: draft.transportDepartureID,
            transportDepartureStopID: draft.transportDepartureStopID,
            dropoffTransportRouteStopID: draft.dropoffTransportRouteStopID,
            passengerCount: draft.passengerCount,
            whatsappCountryCode: draft.whatsappCountryCode,
            whatsappNumber: draft.whatsappNumber,
            contactName: draft.contactName?.trim(),
            contactEmail: draft.contactEmail?.trim(),
        };

        try {
            setSubmitLoading(true);
            setError(null);

            const res = await axios.post(`${apiUrl}/api/transport-route/booking`, payload);

            showNotification({
                title: "Booking created",
                message: `Your transport has been registered (ID: ${res.data.transportRouteBookingID}).`,
            });

            setActive(6);
        } catch (e: any) {
            console.error(e);
            setError(e?.response?.data ?? "Could not create booking.");
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Stack>
            {error && (
                <Alert color="red" title="Error">
                    {error}
                </Alert>
            )}

            <Stepper
                active={active}
                onStepClick={setActive}
                allowNextStepsSelect={false}
                orientation={isMobile ? "vertical" : "horizontal"}
                size={isMobile ? "sm" : "md"}
            >
                <Stepper.Step label="Contact info">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Contact details
                        </Text>

                        <Stack gap="sm">
                            <TextInput
                                label="Name"
                                placeholder="Your full name"
                                value={draft.contactName ?? ""}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        contactName: e.currentTarget.value,
                                    }))
                                }
                                required
                            />

                            <TextInput
                                label="Email"
                                type="email"
                                placeholder="name@example.com"
                                value={draft.contactEmail ?? ""}
                                onChange={(e) =>
                                    setDraft((d) => ({
                                        ...d,
                                        contactEmail: e.currentTarget.value,
                                    }))
                                }
                                required
                            />

                            {!!draft.contactEmail && !emailValid && (
                                <Text size="sm" c="red">
                                    Enter a valid email address.
                                </Text>
                            )}
                        </Stack>

                        <Divider my="sm" />

                        <Text fw={600} mb="xs">
                            WhatsApp number
                        </Text>

                        <Stack gap="sm">
                            <Select
                                label="Country code"
                                placeholder="Select country code"
                                value={draft.whatsappCountryIso2 ?? null}
                                onChange={(iso2) => {
                                    const c = WHATSAPP_COUNTRIES.find(
                                        (x) => x.iso2 === iso2
                                    );

                                    setDraft((d) => ({
                                        ...d,
                                        whatsappCountryIso2: iso2 ?? undefined,
                                        whatsappCountryCode: c?.code ?? "",
                                    }));
                                }}
                                data={whatsappCountryOptions}
                                searchable
                                required
                            />

                            {draft.whatsappCountryCode && (
                                <Text size="sm" c="dimmed">
                                    Selected country: <b>{selectedWhatsappCountry ?? "Unknown"}</b>
                                </Text>
                            )}

                            <TextInput
                                label="WhatsApp number"
                                placeholder="e.g. 12345678"
                                value={draft.whatsappNumber ?? ""}
                                onChange={(e) => {
                                    const raw = e.currentTarget.value;
                                    const cleaned = raw.replace(/[^\d\s-]/g, "");
                                    setDraft((d) => ({
                                        ...d,
                                        whatsappNumber: cleaned,
                                    }));
                                }}
                                required
                            />

                            {!whatsappValid && (
                                <Text size="sm" c="red">
                                    Select country code and enter your WhatsApp number.
                                </Text>
                            )}
                        </Stack>
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Route">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Select route
                        </Text>

                        <OptionGrid
                            options={routeOptions}
                            value={draft.transportRouteID ? String(draft.transportRouteID) : null}
                            onChange={(v) =>
                                setDraft((d) => ({
                                    ...d,
                                    transportRouteID: Number(v),
                                }))
                            }
                        />
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Pickup location">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Select pickup location
                        </Text>

                        {loadingPickupOptions ? (
                            <Text>Loading pickup locations…</Text>
                        ) : pickupLocationOptions.length === 0 ? (
                            <Text c="dimmed">No pickup locations found for the selected route.</Text>
                        ) : (
                            <OptionGrid
                                options={pickupLocationOptions}
                                value={
                                    draft.transportRouteLocationID
                                        ? String(draft.transportRouteLocationID)
                                        : null
                                }
                                onChange={(v) => {
                                    setSelectedPickupDate(undefined);

                                    setDraft((d) => ({
                                        ...d,
                                        transportRouteLocationID: Number(v),
                                        transportDepartureID: undefined,
                                        transportDepartureStopID: undefined,
                                        dropoffTransportRouteStopID: undefined,
                                    }));
                                }}
                            />
                        )}
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Time">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Select pickup date
                        </Text>

                        {loadingPickupOptions ? (
                            <Text>Loading dates…</Text>
                        ) : pickupDateOptions.length === 0 ? (
                            <Text c="dimmed">No dates found for the selected pickup location.</Text>
                        ) : (
                            <OptionGrid
                                options={pickupDateOptions}
                                value={selectedPickupDate ?? null}
                                onChange={(v) => {
                                    setSelectedPickupDate(v);
                                    setDraft((d) => ({
                                        ...d,
                                        transportDepartureID: undefined,
                                        transportDepartureStopID: undefined,
                                        dropoffTransportRouteStopID: undefined,
                                    }));
                                }}
                            />
                        )}

                        <Divider my="md" />

                        <Text fw={600} mb="xs">
                            Select pickup time
                        </Text>

                        {!selectedPickupDate ? (
                            <Text c="dimmed">Please select a date first.</Text>
                        ) : filteredPickupTimeOptions.length === 0 ? (
                            <Text c="dimmed">No times found for the selected date.</Text>
                        ) : (
                            <OptionGrid
                                options={filteredPickupTimeOptions}
                                value={
                                    draft.transportDepartureStopID
                                        ? String(draft.transportDepartureStopID)
                                        : null
                                }
                                onChange={(v) =>
                                    setDraft((d) => ({
                                        ...d,
                                        transportDepartureStopID: Number(v),
                                        dropoffTransportRouteStopID: undefined,
                                    }))
                                }
                            />
                        )}
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Dropoff">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            Where do you want to get off?
                        </Text>

                        {loadingDropoffOptions ? (
                            <Text>Loading dropoff options…</Text>
                        ) : dropoffOptionCards.length === 0 ? (
                            <Text c="dimmed">No dropoff options found.</Text>
                        ) : (
                            <OptionGrid
                                options={dropoffOptionCards}
                                value={
                                    draft.dropoffTransportRouteStopID
                                        ? String(draft.dropoffTransportRouteStopID)
                                        : null
                                }
                                onChange={(v) =>
                                    setDraft((d) => ({
                                        ...d,
                                        dropoffTransportRouteStopID: Number(v),
                                    }))
                                }
                            />
                        )}
                    </Paper>
                </Stepper.Step>

                <Stepper.Step label="Number of passengers">
                    <Paper withBorder p="md" radius="md">
                        <Text fw={600} mb="xs">
                            How many passengers?
                        </Text>

                        <SimpleGrid cols={{ base: 5, sm: 10 }}>
                            {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => {
                                const selected = draft.passengerCount === n;

                                return (
                                    <Paper
                                        key={n}
                                        withBorder
                                        p="xs"
                                        radius="md"
                                        style={{
                                            cursor: "pointer",
                                            textAlign: "center",
                                            borderColor: selected
                                                ? "var(--mantine-primary-color-filled)"
                                                : undefined,
                                            boxShadow: selected
                                                ? "0 0 0 1px var(--mantine-primary-color-filled)"
                                                : undefined,
                                        }}
                                        onClick={() =>
                                            setDraft((d) => ({
                                                ...d,
                                                passengerCount: n,
                                            }))
                                        }
                                    >
                                        <Text fw={600}>{n}</Text>
                                    </Paper>
                                );
                            })}
                        </SimpleGrid>

                        {selectedPickupOption && (
                            <>
                                <Divider my="md" />
                                <Text size="sm" c="dimmed">
                                    Selected pickup: <b>{selectedPickupOption.locationName}</b> •{" "}
                                    <b>{selectedPickupOption.departureDate}</b> •{" "}
                                    <b>{selectedPickupOption.pickupTime}</b>
                                </Text>
                            </>
                        )}

                        {selectedDropoffOption && (
                            <Text size="sm" c="dimmed" mt="xs">
                                Selected dropoff: <b>{selectedDropoffOption.locationName}</b>
                            </Text>
                        )}
                    </Paper>
                </Stepper.Step>

                <Stepper.Completed>
                    <Paper withBorder p="md" radius="md">
                        <Text fw={700}>Confirmed ✅</Text>
                        <Text>Thank you — your booking has been registered.</Text>
                        <Text>
                            {draft.contactName} ({draft.contactEmail})
                        </Text>
                        <Text>
                            WhatsApp: {draft.whatsappCountryCode} {draft.whatsappNumber}
                        </Text>

                        {selectedPickupOption && (
                            <Text mt="sm">
                                Pickup: {selectedPickupOption.locationName} • {selectedPickupOption.departureDate} • {selectedPickupOption.pickupTime}
                            </Text>
                        )}

                        {selectedDropoffOption && (
                            <Text mt="xs">
                                Dropoff: {selectedDropoffOption.locationName}
                            </Text>
                        )}
                    </Paper>
                </Stepper.Completed>
            </Stepper>

            {active < 6 && (
                <Group justify="space-between" mt="md">
                    <Button variant="default" onClick={prev} disabled={active === 0}>
                        Back
                    </Button>

                    {active === 5 ? (
                        <Button onClick={submit} loading={submitLoading}>
                            Confirm booking
                        </Button>
                    ) : (
                        <Button onClick={next} disabled={!isStepValid(active)}>
                            Next
                        </Button>
                    )}
                </Group>
            )}
        </Stack>
    );
}